import { useCallback, useRef } from "react";
import { useAlert, useAppDispatch, useAppSelector } from "@grassrootly/rn-core";
import useBaseRequest from "../../api/BaseRequest";
import { createGroupForm } from "./CreateGroup.types";
import { CreateGroup, CreateGroupPlayer } from "@grassrootly/api/providers/supabase-provider/actions";
import { createGroupActions } from "../../api/create-group/CreateGroupReducers";
import { CreateGroupPayload, ICreateGroupPlayers, IGroup, IGroupPlayers, IPlayerGroup } from "@grassrootly/api";
import moment from "moment";
import { DrawerNavigationProp } from "@react-navigation/drawer";
import { MainStackParamList } from "../../navigation/Navigation.types";
import { createGroupPlayerActions } from "../../api/create-group-player/CreateGroupPlayerReducers";
import { groupsActions } from "../../api/groups/GroupsReducer";

export function useCreateGroup({
  navigation,
}: {
  navigation: DrawerNavigationProp<MainStackParamList, "create-group">;
}) {
  const dispatch = useAppDispatch();
  const playerId = useAppSelector((state) => state.auth.user.id);
  const { success } = useAlert();

  // Store the created group data to use in the second completion handler
  const createdGroupDataRef = useRef<IGroup | null>(null);

  const { loading: createGroupLoading, execute: createGroupRequest } = useBaseRequest(CreateGroup, {
    onCompleted: (data) => {
      if (!data) {
        console.error("CreateGroup: No data returned");
        return;
      }
      console.log("CreateGroup completed, data:", data);
      const filteredData = data.filter((group) => group !== undefined) as IGroup[];
      const groupId = filteredData[0].id;
      console.log("Created group ID:", groupId);
      createdGroupDataRef.current = filteredData[0]; // Store the group data
      createGroupPlayer(groupId);
      dispatch(createGroupActions.setCreateGroup(filteredData));
    },
    onError: (error) => {
      console.error("CreateGroup error:", error);
    },
  });

  const { loading: createGroupPlayerLoading, execute: createGroupPlayerRequest } = useBaseRequest(CreateGroupPlayer, {
    onCompleted: (data: Partial<ICreateGroupPlayers>[]) => {
      if (!data) {
        console.error("CreateGroupPlayer: No data returned");
        return;
      }
      const filteredData = data.filter((group) => group !== undefined) as IGroup[];
      const id = Number(filteredData[0]?.group_id);
      
      // Update the create group player reducer
      dispatch(createGroupPlayerActions.setCreateGroupPlayer(filteredData));
      
      // Also update the main groups reducer with membership data so the group screen recognizes the user as owner
      if (createdGroupDataRef.current) {
        const playerGroupData: IPlayerGroup = {
          ...createdGroupDataRef.current,
          is_owner: true, // User is the owner since they created the group
          is_member: true, // User is automatically a member
        };
        dispatch(groupsActions.setPlayerGroup(playerGroupData));
      }
      
      success({ message: "Group successfully created." });
      navigation.navigate("group", { id });
    },
    onError: (error) => {
      console.error("CreateGroupPlayer error:", error);
    },
  });

  const onSubmitCreateGroup = useCallback(
    async (groupName: string | { name: string }) => {
      try {
        const name = typeof groupName === "string" ? groupName : groupName.name;
        const groupData: CreateGroupPayload = {
          createGroup: [
            {
              name: name,
              group_logo: null,
              created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
              id: 0,
              playerId: playerId,
            },
          ],
        };
        createGroupRequest(groupData);
      } catch (error) {
        console.error("Error creating group:", error);
      }
    },
    [createGroupRequest, playerId]
  );

  // function for creating group player
  const createGroupPlayer = useCallback(
    async (groupId: number) => {
      try {
        console.log("Creating GroupPlayer with:", { groupId, playerId });
        const groupPlayerData: IGroupPlayers = {
          group_id: groupId,
          player_id: playerId,
          is_owner: true,
          created_at: moment().format("YYYY-MM-DD HH:mm:ss"),
          id: 0,
        };
        console.log("GroupPlayer data:", groupPlayerData);
        createGroupPlayerRequest(groupPlayerData);
      } catch (error) {
        console.error("Error creating GroupPlayer:", error);
      }
    },
    [createGroupPlayerRequest, playerId]
  );

  return { loading: createGroupLoading || createGroupPlayerLoading, onSubmitCreateGroup, createGroupForm };
}
